import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, LogOut, Mail, Phone, MapPin, Paperclip, RefreshCw, Inbox } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Btn } from "@/components/Btn";
import { Logo } from "@/components/Logo";
import Seo from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ["nouveau", "en cours", "traité", "archivé"];
const statusColor = {
  nouveau: "border-signature/40 text-signature bg-signature/10",
  "en cours": "border-electric/40 text-electric bg-electric/10",
  traité: "border-emerald-500/40 text-emerald-400 bg-emerald-500/10",
  archivé: "border-white/20 text-chrome-mid bg-white/5",
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user === false) nav("/admin/login");
  }, [user, nav]);

  const load = () => {
    setLoading(true);
    axios.get(`${API}/admin/quotes`, { withCredentials: true })
      .then((res) => setQuotes(res.data))
      .catch(() => toast.error("Impossible de charger les demandes"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { if (user) load(); }, [user]);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/quotes/${id}`, { status }, { withCredentials: true });
      setQuotes((qs) => qs.map((q) => (q.id === id ? { ...q, status } : q)));
      toast.success("Statut mis à jour");
    } catch {
      toast.error("Échec de la mise à jour");
    }
  };

  const doLogout = async () => { await logout(); nav("/admin/login"); };

  if (user === null) {
    return <div className="min-h-screen bg-ink-900 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-signature" /></div>;
  }

  const counts = STATUSES.reduce((acc, s) => ({ ...acc, [s]: quotes.filter((q) => q.status === s).length }), {});

  return (
    <div className="min-h-screen bg-ink-900">
      <Seo title="Tableau de bord | Ronalp Enseigne" description="Administration des demandes de devis." path="/admin" noindex />
      <header className="border-b border-white/10 bg-ink-800 sticky top-0 z-10">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <Logo className="h-8" />
          <div className="flex items-center gap-3">
            <button onClick={load} className="p-2 text-chrome-mid hover:text-white" aria-label="Rafraîchir" data-testid="admin-refresh"><RefreshCw className="h-4 w-4" /></button>
            <Btn onClick={doLogout} variant="secondary" size="sm" data-testid="admin-logout"><LogOut className="h-4 w-4" /> Déconnexion</Btn>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white">Demandes de devis</h1>
            <p className="mt-1 text-sm text-chrome-mid">{quotes.length} demande(s) au total</p>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          {STATUSES.map((s) => (
            <div key={s} className={`rounded-xl border px-5 py-4 ${statusColor[s]}`}>
              <p className="text-2xl font-bold">{counts[s] || 0}</p>
              <p className="text-xs font-mono uppercase tracking-wider capitalize">{s}</p>
            </div>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-signature" /></div>
        ) : quotes.length === 0 ? (
          <div className="rounded-2xl border border-white/10 bg-ink-800 p-16 text-center" data-testid="admin-empty">
            <Inbox className="mx-auto h-12 w-12 text-chrome-dark" />
            <p className="mt-4 text-slate-400">Aucune demande de devis pour le moment.</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="admin-quotes-list">
            {quotes.map((q) => (
              <div key={q.id} className="rounded-2xl border border-white/10 bg-ink-800 p-6" data-testid={`quote-${q.id}`}>
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h2 className="text-lg font-semibold text-white">{q.name}</h2>
                      {q.company && <span className="text-sm text-chrome-mid">— {q.company}</span>}
                      <span className={`rounded-full border px-2.5 py-0.5 text-xs font-mono uppercase ${statusColor[q.status] || ""}`}>{q.status}</span>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1.5 text-sm text-slate-400">
                      <a href={`mailto:${q.email}`} className="flex items-center gap-1.5 hover:text-white"><Mail className="h-3.5 w-3.5" /> {q.email}</a>
                      <a href={`tel:${q.phone}`} className="flex items-center gap-1.5 hover:text-white"><Phone className="h-3.5 w-3.5" /> {q.phone}</a>
                      {q.city && <span className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> {q.city}</span>}
                    </div>
                    <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
                      {q.project_type && <p className="text-slate-400"><span className="text-chrome-dark">Type :</span> {q.project_type}</p>}
                      {q.dimensions && <p className="text-slate-400"><span className="text-chrome-dark">Dimensions :</span> {q.dimensions}</p>}
                      {q.budget && <p className="text-slate-400"><span className="text-chrome-dark">Budget :</span> {q.budget}</p>}
                      {q.desired_date && <p className="text-slate-400"><span className="text-chrome-dark">Date :</span> {q.desired_date}</p>}
                    </div>
                    <p className="mt-3 text-sm text-slate-300 bg-ink-700 rounded-lg p-4">{q.description}</p>
                    {q.files && q.files.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {q.files.map((f) => (
                          <a key={f.id} href={`${API}/files/${f.storage_path}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 bg-ink-700 px-3 py-1.5 text-xs text-slate-300 hover:text-white hover:border-signature/40 transition-all" data-testid={`quote-file-${f.id}`}>
                            <Paperclip className="h-3.5 w-3.5" /> {f.original_filename}
                          </a>
                        ))}
                      </div>
                    )}
                    <p className="mt-3 text-xs text-chrome-dark font-mono">{new Date(q.created_at).toLocaleString("fr-FR")}</p>
                  </div>
                  <div className="shrink-0">
                    <label className="block text-xs font-mono uppercase tracking-wider text-chrome-dark mb-2">Statut</label>
                    <select
                      value={q.status}
                      onChange={(e) => updateStatus(q.id, e.target.value)}
                      className="rounded-lg bg-ink-700 border border-chrome-dark text-white px-3 py-2 text-sm focus:border-signature outline-none"
                      data-testid={`quote-status-${q.id}`}
                    >
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
